"use strict";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useMismatchContext } from "features/smartWallet/mismatch/MismatchContext";
import { useMakeAccountMismatchQueryOptions } from "features/smartWallet/mismatch/useMakeAccountMismatchQueryOptions";
import { useEvent, usePrevious } from "utilities/src/react/hooks";
export function useIsMismatchAccountQuery(input) {
  const getQueryOptions = useCurrentAccountMismatchQueryOptions();
  return useQuery(getQueryOptions(input?.chainId));
}
export function useHasAccountMismatchOnAnyChain() {
  const queries = useAllChainMismatchQueries();
  return useMemo(() => {
    return queries.some((query) => query.data?.hasMismatch);
  }, [queries]);
}
export function useHasAccountMismatchCallback() {
  const queries = useAllChainMismatchQueries();
  return useEvent((chainId) => {
    if (!chainId) {
      return queries.some((query) => query.data?.hasMismatch);
    }
    return Boolean(queries.find((query) => query.data?.chainId === chainId)?.data?.hasMismatch);
  });
}
export function useCurrentAccountChainMismatchEffect() {
  const account = useMismatchContext().account;
  const getQueryOptions = useCurrentAccountMismatchQueryOptions();
  const queryOptions = getQueryOptions(account.chainId);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!account.address || !account.chainId) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: queryOptions.queryKey }).catch(() => {
    });
  }, [account.address, account.chainId, queryOptions, queryClient]);
}
export function useOnConnectCheckAllAccountChainMismatchEffect() {
  const { account, onHasAnyMismatch, isTestnetModeEnabled } = useMismatchContext();
  const currentMode = isTestnetModeEnabled ? "testnet" : "mainnet";
  const previousMode = usePrevious(currentMode);
  const { mutate, isPending, isSuccess, isError } = useAllAcountChainMismatchMutation({ onHasAnyMismatch });
  const checkedAddress = useRef(void 0);
  const checkAllAccountChainMismatch = useEvent(() => {
    if (isPending || isSuccess || isError) {
      return;
    }
    mutate();
  });
  useEffect(() => {
    if (previousMode !== currentMode) {
      checkAllAccountChainMismatch();
      return;
    }
  }, [currentMode, previousMode, checkAllAccountChainMismatch]);
  useEffect(() => {
    if (account.address && account.address !== checkedAddress.current) {
      checkAllAccountChainMismatch();
      checkedAddress.current = account.address;
    }
  }, [account.address, checkAllAccountChainMismatch]);
}
function useAllChainMismatchQueries() {
  const queryOptions = useAllAccountChainMismatchQueryOptions();
  return useQueries({ queries: queryOptions });
}
function useCurrentAccountMismatchQueryOptions() {
  const mismatchContext = useMismatchContext();
  return useMakeAccountMismatchQueryOptions({
    hasMismatch: mismatchContext.mismatchCallback,
    account: { address: mismatchContext.account.address, chainId: mismatchContext.account.chainId }
  });
}
function useAllAccountChainMismatchQueryOptions() {
  const chains = useMismatchContext().chains;
  const getQueryOptions = useCurrentAccountMismatchQueryOptions();
  return useMemo(() => {
    return chains.map((chainId) => getQueryOptions(chainId));
  }, [getQueryOptions, chains]);
}
const useAllAcountChainMismatchMutation = (ctx) => {
  const queryOptions = useAllAccountChainMismatchQueryOptions();
  const queryClient = useQueryClient();
  const account = useMismatchContext().account;
  return useMutation({
    retry: (failureCount, error) => {
      const isRetryable = isRetryableError(error);
      switch (isRetryable) {
        case "retry":
          return true;
        case "noRetry":
          return false;
        default:
          return failureCount < 3;
      }
    },
    mutationFn: async () => {
      if (!account.address) {
        return [];
      }
      return Promise.all(
        queryOptions.map(
          (query) => (
            // we fetch via the query so we can cache the result in RQ
            // staleTime is set to 0 so we don't use cached data
            queryClient.fetchQuery({ ...query, staleTime: 0 })
          )
        )
      );
    },
    onSuccess: (data) => {
      if (!account.address) {
        return;
      }
      const hasMismatch = data.some((result) => result.hasMismatch);
      if (hasMismatch) {
        ctx.onHasAnyMismatch();
      }
    }
  });
};
function isRetryableError(error) {
  const errorMessage = error.message;
  const found = Object.keys(ERROR_MAP).find((key) => errorMessage.toLowerCase().includes(key.toLowerCase()));
  const existing = found ? ERROR_MAP[found] : void 0;
  if (existing) {
    return existing.retry ? "retry" : "noRetry";
  }
  return "unknown";
}
const ERROR_MAP = {
  UnknownRPCError: {
    retry: false
  },
  "Not implemented": {
    retry: false
  }
};
